/*
 *
 * Report reducer
 *
 */

import { fromJS, Set } from 'immutable'
import {
  DISMISS_NOTIFICATION,
  DISMISS_NOTIFICATION_ERROR,
  DELETE_NOTIFICATION,
  DELETE_NOTIFICATION_ERROR,
  DISMISS_ALL_NOTIFICATIONS,
  GET_NOTIFICATIONS_SUCCESS,
  DISMISS_ALL_NOTIFICATIONS_ERROR,
  DISMISS_ALL_NOTIFICATIONS_SUCCESS,
} from './constants'

const initialState = fromJS({
  notifications: undefined,
  error: false,
  loading: false,
}).set('removedNotificationIds', Set())

const notificationReducer = (state = initialState, action) => {
  switch (action.type) {
    case DISMISS_ALL_NOTIFICATIONS:
      return state.set('loading', true).set('error', false)
    case DISMISS_ALL_NOTIFICATIONS_ERROR:
      return state.set('loading', false).set('error', action.error)
    case DISMISS_ALL_NOTIFICATIONS_SUCCESS:
      return state.set('loading', false).delete('notifications')
    case DISMISS_NOTIFICATION:
    case DELETE_NOTIFICATION:
      // Removed both from the visible list and tombstoned here, at dispatch
      // time, rather than waiting for the DELETE/DISMISS request to
      // resolve. The periodic notification poll (see Notifications/index.js)
      // can have a request already in flight from before this action, whose
      // response reflects pre-removal server state and would otherwise
      // resurrect this notification when GET_NOTIFICATIONS_SUCCESS replaces
      // the list below. The tombstone keeps it hidden regardless of what a
      // stale fetch says, until DISMISS/DELETE actually fails (see below).
      return state
        .update('notifications', (notifications) =>
          notifications
            ? notifications.filter(
                (notification) => notification.id !== action.notificationId
              )
            : notifications
        )
        .update('removedNotificationIds', (ids) =>
          ids.add(action.notificationId)
        )
    case DISMISS_NOTIFICATION_ERROR:
    case DELETE_NOTIFICATION_ERROR:
      // The removal didn't actually happen server-side, so let the
      // notification be visible again once the next fetch lands.
      return action.notificationId
        ? state.update('removedNotificationIds', (ids) =>
            ids.delete(action.notificationId)
          )
        : state
    case GET_NOTIFICATIONS_SUCCESS: {
      if (!action.notifications) {
        return state.set('notifications', action.notifications)
      }
      const removedIds = state.get('removedNotificationIds')
      const notifications = removedIds.isEmpty()
        ? action.notifications
        : action.notifications.filter(
            (notification) => !removedIds.has(notification.id)
          )
      // Prune tombstones once the server confirms a removal: if this
      // payload no longer contains a removed id, every future fetch
      // will agree (nothing un-deletes a notification), so there's no
      // reason to keep it tombstoned. A payload that still contains the
      // id is exactly the stale-fetch case this tombstone exists for,
      // so it's kept - the filter above already hid it from this
      // payload regardless.
      const fetchedIds = Set(action.notifications.map((n) => n.id))
      return state
        .set('notifications', notifications)
        .update('removedNotificationIds', (ids) => ids.intersect(fetchedIds))
    }
    default:
      return state
  }
}

export default notificationReducer
